import { Fragment, type JSX, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { ComplianceRequirementsApi } from 'Api/ComplianceRequirements/ComplianceRequirementsApi';
import { ControlsApi } from 'Api/Controls/ControlsApi';
import { Button } from 'Components/Buttons/Buttons';
import { ComplianceRequirementOverallStatusTooltip, ComplianceRequirementStatusIcon } from 'Components/ComplianceRequirementStatus/ComplianceRequirementStatus';
import { PageBackground } from 'Components/Containers/PageBackground/PageBackground';
import { PageCell } from 'Components/Containers/PageCell/PageCell';
import { PageContent } from 'Components/Containers/PageContent/PageContent';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { ModalHeader } from 'Components/Modal/ModalHeader/ModalHeader';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { ICON_ADD_CREATE } from 'Config/Icons';
import { COMPLIANCE_REQUIREMENTS } from 'Config/Paths';
import { makeComplianceRequirementHumanReadable } from 'Helpers/ComplianceRequirementFormatter/ComplianceRequirementFormatter';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { useControlMappingItems } from 'Hooks/ControlMapping';
import { ComplianceRequirementWithAllControlStatusesResponse, ControlSetDetails, StatusCategory, UpdateAssignedControlRequest } from 'Models/ComplianceRequirements';

import { ModifyControlRow, ModifyControlRowProps } from './ModifyControlRow/ModifyControlRow';
import { ModifyRequirementAssociatedControlsModal, ModifyRequirementAssociatedControlsModalProps } from './ModifyRequirementAssociatedControlsModal/ModifyRequirementAssociatedControlsModal';
import styles from './RequirementDetails.module.css';

export interface RequirementDetailsProps {
    complianceRequirementsApi: ComplianceRequirementsApi;
    controlsApi: ControlsApi;
}

interface PathParams {
    regulationName: string;
    requirement: string;
}

enum Modals {
    ModifyRequirementAssociatedControlsModal,
    DeleteRequirementAssociatedControlsModal,
    None,
}

export const RequirementDetails = (props: RequirementDetailsProps): JSX.Element => {
    const { getComplianceStatusDefinition } = useCachedData();
    const [error, setError] = useState<string>();
    const [requirementDetails, setRequirementDetails] = useState<ComplianceRequirementWithAllControlStatusesResponse>();
    const [showModal, setShowModal] = useState<Modals>(Modals.None);
    const [controlMappingItems, controlMappingItemsError] = useControlMappingItems(props.controlsApi);
    const [selectedControlSet, setSelectedControlSet] = useState<string[]>([]);
    const [selectedControlSetIndex, setSelectedControlSetIndex] = useState<number>();
    const { regulationName, requirement } = useParams<keyof PathParams>() as PathParams;
    const hideModal = () => setShowModal(Modals.None);

    const getRequirementDetails = useCallback(async (): Promise<void> => {
        try {
            const requirementDetailsResponse = await props.complianceRequirementsApi.getRequirementDetails(regulationName, requirement);
            setRequirementDetails(requirementDetailsResponse.data);
        } catch (error) {
            setError(error.message);
        }
    }, [props.complianceRequirementsApi, regulationName, requirement]);

    //returns control IDs
    const mappedControlsDetailsToUpdateRequest = (): UpdateAssignedControlRequest => {
        return {
            assigned_control_sets: requirementDetails!.assigned_control_sets.map((list) => list.control_list.map((control) => control.control.identifier)),
        };
    };

    const addControlSet = async (controls: string[]): Promise<void> => {
        const request = mappedControlsDetailsToUpdateRequest();
        request.assigned_control_sets.push(controls);
        await props.complianceRequirementsApi.updateComplianceRequirementControls(regulationName, requirement, request);
        await getRequirementDetails();
    };

    const modifyControlSet = async (controls: string[]): Promise<void> => {
        const request = mappedControlsDetailsToUpdateRequest();
        request.assigned_control_sets.splice(selectedControlSetIndex!, 1, controls);
        await props.complianceRequirementsApi.updateComplianceRequirementControls(regulationName, requirement, request);
        await getRequirementDetails();
    };

    const deleteControlSet = async (): Promise<string> => {
        const request = mappedControlsDetailsToUpdateRequest();
        request.assigned_control_sets.splice(selectedControlSetIndex!, 1);
        await props.complianceRequirementsApi.updateComplianceRequirementControls(regulationName, requirement, request);
        await getRequirementDetails();
        return 'Control set deleted.';
    };

    useEffect(() => {
        getRequirementDetails();
    }, [getRequirementDetails]);

    const openModifyControlSetModal = (selectedControlSet: string[], index: number | undefined) => {
        setSelectedControlSet(selectedControlSet);
        setSelectedControlSetIndex(index);
        setShowModal(Modals.ModifyRequirementAssociatedControlsModal);
    };

    const openDeleteControlSetModal = (selectedControlSet: string[], index: number) => {
        setSelectedControlSet(selectedControlSet);
        setSelectedControlSetIndex(index);
        setShowModal(Modals.DeleteRequirementAssociatedControlsModal);
    };

    const regulatoryGroup = (): JSX.Element[] => {
        return requirementDetails!.assigned_control_sets.map((list, index) => {
            const modifyControlRowProps: ModifyControlRowProps = {
                mappedControlSet: list,
                regulation: requirementDetails!.regulation,
                requirementId: requirementDetails!.identifier,
                openModifyControlSetModal: (controlSet) => openModifyControlSetModal(controlSet, index),
                openDeleteControlSetModal: (controlSet) => openDeleteControlSetModal(controlSet, index),
            };
            if (index === 0) {
                return <ModifyControlRow {...modifyControlRowProps} key={index} />;
            } else {
                return (
                    <Fragment key={index}>
                        <Text variant="Text2" noStyles>
                            AND
                        </Text>
                        <ModifyControlRow {...modifyControlRowProps} />
                    </Fragment>
                );
            }
        });
    };

    /**
     * Count the number of unique Control IDs.
     * @returns number
     */
    const countUniqueControlIds = (controlSetDetailsList: ControlSetDetails[]): number => {
        const uniqueControlIds = new Set();
        controlSetDetailsList.forEach((controlSetDetails) => {
            controlSetDetails.control_list.forEach((operationalControlWithStatus) => {
                uniqueControlIds.add(operationalControlWithStatus.control.identifier);
            });
        });
        return uniqueControlIds.size;
    };

    const modifyRequirementAssociatedControlModalProps: ModifyRequirementAssociatedControlsModalProps | undefined = controlMappingItems
        ? {
              hideModal: hideModal,
              saveControlSet: selectedControlSet.length === 0 ? addControlSet : modifyControlSet,
              currentMappedControlIdentifiers: selectedControlSet,
              controlMappingItems: controlMappingItems,
              controlMappingItemsError: controlMappingItemsError,
              updateRequirementDetails: getRequirementDetails,
          }
        : undefined;

    if (requirementDetails && modifyRequirementAssociatedControlModalProps) {
        return (
            <>
                {showModal === Modals.ModifyRequirementAssociatedControlsModal && <ModifyRequirementAssociatedControlsModal {...modifyRequirementAssociatedControlModalProps} />}
                {showModal === Modals.DeleteRequirementAssociatedControlsModal && (
                    <ConfirmationModal operationType={ConfirmationModalOperationType.DELETE_WITHOUT_TYPING_TO_CONFIRM} headerText="Delete Control Set" areYouSureText="Are you sure you want to delete the control set?" performOperation={deleteControlSet} hideModal={() => setShowModal(Modals.None)}>
                        <>
                            <Text>The set containing these controls will be unmapped from the requirement:</Text>
                            <ul>
                                {selectedControlSet.map((control, index) => (
                                    <Text key={index} color="blue">
                                        <li>{control}</li>
                                    </Text>
                                ))}
                            </ul>
                        </>
                    </ConfirmationModal>
                )}

                <PageBackground color="grey">
                    <PageContent>
                        <Breadcrumb textColor="blue">
                            <BreadcrumbLink link={`/${COMPLIANCE_REQUIREMENTS}#${regulationName}`}>Compliance Requirements</BreadcrumbLink>
                            <BreadcrumbText>{`${regulationName}: ${requirement}`}</BreadcrumbText>
                        </Breadcrumb>
                        <div className={styles.headerContainer}>
                            <Text variant="Header1" noStyles>
                                {makeComplianceRequirementHumanReadable(requirementDetails.regulation, requirementDetails.identifier)}
                            </Text>
                        </div>
                    </PageContent>
                </PageBackground>
                <PageBackground color="white">
                    <PageContent>
                        <PageCell>
                            <ModalHeader text="Requirement Overview" />
                            <Text variant="Header4">Overall Requirement Status</Text>
                            <div className={styles.overviewRating}>
                                {requirementDetails.overall_status === undefined ? (
                                    <>
                                        <Text variant="Header2" color="blue" noStyles>
                                            <span className={styles.iconOutcomeContainer}>-</span>
                                            Awaiting Assessments
                                        </Text>
                                        <div className={styles.tooltip}>
                                            <ComplianceRequirementOverallStatusTooltip assignedControlSetCount={requirementDetails.assigned_control_sets.length} overallStatus={requirementDetails.overall_status} regulation={requirementDetails.regulation} requirementId={requirementDetails.identifier} />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/** Since the icon for StatusCategory.UNKNOWN is the same as the tooltip icon, this prevents showing them nearby to avoid confusion. The status text is sufficient. */}
                                        {requirementDetails.overall_status !== StatusCategory.UNKNOWN && (
                                            <div className={styles.iconOutcomeContainer}>
                                                <ComplianceRequirementStatusIcon regulation={requirementDetails.regulation} status={requirementDetails.overall_status} />
                                            </div>
                                        )}
                                        <Text variant="Header2" color="blue" noStyles>
                                            {getComplianceStatusDefinition(requirementDetails.regulation, requirementDetails.overall_status)?.value}
                                        </Text>
                                        <div className={styles.tooltip}>
                                            <ComplianceRequirementOverallStatusTooltip assignedControlSetCount={requirementDetails.assigned_control_sets.length} overallStatus={requirementDetails.overall_status} regulation={requirementDetails.regulation} requirementId={requirementDetails.identifier} />
                                        </div>
                                    </>
                                )}
                            </div>
                            <>
                                {requirementDetails.parent_context && (
                                    <>
                                        <Text variant="Header4">Parent Requirement Text</Text>
                                        <Text color="blue">{requirementDetails.parent_context}</Text>
                                    </>
                                )}
                                <Text variant="Header4">Requirement Text</Text>
                                <Text color="blue" variant="Text3">
                                    {requirementDetails.text}
                                </Text>
                            </>
                            <div className={styles.controlsDetails}>
                                <div>
                                    <Text variant="Header4">Number of Controls Mapped</Text>
                                    <Text color="blue" variant="Text3" noStyles>
                                        {countUniqueControlIds(requirementDetails.assigned_control_sets)}
                                    </Text>
                                </div>
                                <div>
                                    <Text variant="Header4">Last Updated</Text>
                                    {requirementDetails.last_updated ? (
                                        <Text color="blue" variant="Text3" noStyles>
                                            {iso8601ToUsDateShort(requirementDetails.last_updated)}
                                        </Text>
                                    ) : (
                                        <Text color="blue" variant="Text3" noStyles>
                                            N/A
                                        </Text>
                                    )}
                                </div>
                            </div>
                        </PageCell>
                        <div className={styles.textButtonRow}>
                            <Button variant="primary" onClick={() => openModifyControlSetModal([], undefined)} fontAwesomeImage={ICON_ADD_CREATE}>
                                Add Control Set
                            </Button>
                        </div>
                        {regulatoryGroup()}
                    </PageContent>
                </PageBackground>
            </>
        );
    } else if (error) {
        return <Text color="darkBlue">{error}</Text>;
    } else return <Placeholder />;
};
